import { useMemo, useRef, useEffect, useState } from 'react';
import { fa, toLatin, toman, qty } from './fa';
import type { CatalogueItem, Shelf } from './types';
import { CATEGORY_ALL, onShelf } from './shelves';

/*
 * The sale screen — where a cashier spends the whole shift.
 *
 * Three layouts, because three different shops are using the same till. A
 * grocer with four thousand SKUs lives in the search box and never browses; a
 * café with twenty drinks wants big tiles and never types; a hardware shop
 * wants a dense list it can scan with its eyes. The design makes this a setting
 * rather than a guess, and search is the default because it is the only one
 * that works at any catalogue size.
 *
 * The barcode scanner is the real primary input. It behaves as a keyboard:
 * it types the code into whatever has focus and presses Enter. That is why the
 * search field takes focus on mount and takes it back after every add — a till
 * where the scanner silently does nothing because focus drifted is a till the
 * shop stops trusting within a day.
 */

export type Layout = 'search' | 'grid' | 'list';


/*
 * A product's picture on a tile, or nothing at all.
 *
 * No picture and a picture that fails to load render IDENTICALLY, on purpose.
 * The browser's broken-image glyph on a till tile is worse than a tile with no
 * photograph, and a grey placeholder that appears only for SOME products makes
 * a shop's own menu look half-finished. Returning null leaves the tile exactly
 * as it was before this existed.
 *
 * Failing to load is the ordinary case, not an error worth surfacing: the URL
 * is in the cloud and this till sells for days on a dropped line. Nothing about
 * ringing up a sale may depend on it, which is why there is no retry, no
 * spinner and no log line.
 *
 * Fixed width AND height, with the tile already sized by its text, so the grid
 * does not reflow as pictures arrive one by one over a slow shop connection.
 */
function Thumb({ url, size }: { url?: string; size: number }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return null;
  return (
    <img
      src={url}
      /* Decorative: the button's own aria-label already names the product and
         its price, so alt text here would read the name twice. */
      alt=""
      loading="lazy"
      decoding="async"
      /* A cashier's palm resting on a touch screen drags an image by default,
         which looks like the tile came loose. */
      draggable={false}
      onError={() => setFailed(true)}
      style={{
        flex: `0 0 ${size}px`, width: size, height: size,
        /* contain, not cover: show the whole picture.
           cover fills the square by cropping whatever does not fit, and a
           shop's product photos are not square - a bottle shot loses its top
           and bottom, and two cuts of meat that differ at the edges of the
           frame come out looking identical on the tile. A cashier picking by
           picture needs the whole thing.
           The box stays exactly square so the grid still does not reflow as
           pictures arrive one by one; the faint background below becomes the
           letterbox, which is why it was already there. */
        borderRadius: 8, objectFit: 'contain',
        background: 'var(--pos-rule-faint)', pointerEvents: 'none',
      }}
    />
  );
}

function stockChip(item: CatalogueItem): { label: string; short: string; style: React.CSSProperties } {
  const base: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, padding: '2px 7px',
    borderRadius: 6, whiteSpace: 'nowrap',
  };

  // A made-to-order item has no shelf of its own; showing it a count of zero
  // would read as "out of stock" for something the kitchen can make all day.
  if (!item.tracksStock) {
    return {
      label: 'خدماتی', short: '—',
      style: { ...base, background: 'var(--luma-surface-hover)', color: 'var(--luma-muted)' },
    };
  }
  if (item.onHand <= 0) {
    return {
      label: 'تمام شد', short: 'تمام شد',
      style: { ...base, background: 'var(--luma-tint-danger-bg)', color: 'var(--luma-danger)' },
    };
  }
  if (item.onHand <= 5) {
    return {
      label: `${qty(item.onHand)} عدد`, short: qty(item.onHand),
      style: { ...base, background: 'var(--luma-accent-tint)', color: 'var(--luma-accent-tint-text)' },
    };
  }
  return {
    label: `${qty(item.onHand)} عدد`, short: qty(item.onHand),
    style: { ...base, background: 'var(--luma-surface-hover)', color: 'var(--luma-muted)' },
  };
}

export function SaleScreen({
  catalogue, shelves, loading, query, onQuery, category, onCategory,
  layout, onLayout, onAdd, notFoundCode, onDismissNotFound, oversoldAdd,
  weighing, onWeigh, offline, queued,
}: {
  catalogue: CatalogueItem[];
  loading: boolean;
  query: string;
  onQuery: (v: string) => void;
  /** The selected shelf's id, or CATEGORY_ALL. Never its name — see the filter. */
  category: string;
  /** The shop's shelves, in the shop's order, normalised by Till. */
  shelves: Shelf[];
  onCategory: (c: string) => void;
  layout: Layout;
  onLayout: (l: Layout) => void;
  onAdd: (item: CatalogueItem) => void;
  notFoundCode: string | null;
  onDismissNotFound: () => void;
  /** Named the moment it is added, beside the scan field rather than only in the cart. */
  oversoldAdd: string | null;
  weighing: boolean;
  onWeigh: () => void;
  offline: boolean;
  queued: number;
}) {
  const searchRef = useRef<HTMLInputElement>(null);

  /*
   * Focus lives in the search box, and comes back to it.
   *
   * F2 is in the design as the shortcut, but the more important behaviour is
   * the automatic one: a scanner is a keyboard, and a keyboard types into
   * whatever has focus. Every moment focus sits somewhere else is a moment a
   * scan goes nowhere and the cashier does not find out until they look up.
   */
  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /*
   * The chips are the shop's arrangement, as the server sent it.
   *
   * This used to scan the catalogue and sort the names, which made the till the
   * sixth place in the product that answered "what are this shop's categories?"
   * its own way — and the six disagreed. Alphabetical is not the order the shop
   * chose, and a rename that has reached some products and not others shows up
   * as two chips rather than one.
   */
  const categories = useMemo<Shelf[]>(
    () => [{ id: CATEGORY_ALL, name: 'همه' }, ...shelves],
    [shelves],
  );

  const q = query.trim();
  const searching = q.length > 0;

  /*
   * Search matches name, product name, barcode and SKU.
   *
   * Latin-folded on both sides: a scanner emits ASCII digits while the
   * catalogue may hold Persian ones, and matching them literally means the
   * fastest input in the shop finds nothing.
   */
  const matches = useMemo(() => {
    if (!searching) return [];
    const needle = toLatin(q).toLowerCase();
    return catalogue.filter((i) => {
      const hay = [i.name, i.productName, i.barcode ?? '', i.sku ?? ''].join(' ');
      return toLatin(hay).toLowerCase().includes(needle);
    });
  }, [catalogue, q, searching]);

  const browsing = useMemo(
    /*
     * Matched on identity, with the name as a fallback.
     *
     * Keying on the name alone meant a category renamed in the panel silently
     * matched nothing: the cashier's current chip kept its old label, the grid
     * emptied, and only tapping another chip recovered it. The id survives a
     * rename. The name is still checked because an item whose category row has
     * not replicated carries an empty categoryId, and dropping it from its own
     * shelf would be a worse bug than the one this fixes.
     */
    () => catalogue.filter((i) => onShelf(i, category, shelves)),
    [catalogue, category, shelves],
  );

  function add(item: CatalogueItem) {
    onAdd(item);
    onQuery('');
    searchRef.current?.focus();
  }

  const tall = layout === 'search';

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: 14, gap: 12 }}>
      {offline && (
        <div
          style={{
            flex: 'none', background: 'var(--luma-tint-warning-bg)',
            border: '1px solid var(--luma-tint-warning-border)', borderRadius: 12,
            padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div
            style={{
              width: 32, height: 32, borderRadius: 9, background: 'var(--pos-accent-deep)',
              display: 'grid', placeItems: 'center',
              color: 'var(--luma-accent-tint-text)', fontWeight: 800,
            }}
          >
            !
          </div>
          <div style={{ flex: 1, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>اتصال قطع است — صندوق کامل کار می‌کند</div>
            <div style={{ fontSize: 12.5, color: '#8A6A4A' }}>
              فاکتورها روی دستگاه ذخیره و پس از وصل شدن خودکار همگام می‌شوند.
            </div>
          </div>
          {queued > 0 && (
            <div
              style={{
                fontSize: 12.5, fontWeight: 700, color: 'var(--luma-accent-tint-text)',
                background: 'var(--pos-accent-deep)', borderRadius: 8, padding: '5px 10px',
              }}
            >
              {fa(queued)} فاکتور در صف همگام‌سازی
            </div>
          )}
        </div>
      )}

      {/* --- search, scan, weigh ------------------------------------------ */}
      <div style={{ flex: 'none', display: 'flex', gap: 10 }}>
        <div
          style={{
            flex: 1, minWidth: 0, height: tall ? 60 : 52,
            background: 'var(--luma-card)', border: '1px solid var(--luma-line-strong)',
            borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px',
            // The permanent orange ring: the design marks the hot path so the
            // eye lands on it without hunting.
            boxShadow: '0 1px 2px rgba(32,30,29,.05), 0 0 0 4px rgba(249,124,21,.10)',
          }}
        >
          <span style={{ fontSize: 18, color: 'var(--luma-placeholder)' }}>⌕</span>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="نام کالا، کد یا بارکد را بزنید…"
            style={{
              flex: 1, minWidth: 0, height: '100%', border: 0, background: 'transparent',
              fontSize: 17, fontWeight: 500, color: 'var(--luma-ink)',
            }}
          />
          {q.length > 0 && (
            <button
              onClick={() => { onQuery(''); searchRef.current?.focus(); }}
              aria-label="پاک کردن جست‌وجو"
              style={{ width: 44, height: 44, borderRadius: 9, color: 'var(--luma-muted)', fontSize: 16 }}
            >
              ✕
            </button>
          )}
          <div
            style={{
              fontSize: 11.5, color: 'var(--luma-placeholder)',
              borderInlineStart: '1px solid var(--pos-rule-strong)', paddingInlineStart: 10,
            }}
          >
            F2 جست‌وجو
          </div>
        </div>

        {/* The scan button focuses the field rather than pretending to scan.
            The hardware types into it; there is nothing for software to do. */}
        <button
          onClick={() => { searchRef.current?.focus(); searchRef.current?.select(); }}
          style={{
            width: tall ? 84 : 72, height: tall ? 60 : 52, borderRadius: 12,
            background: 'var(--luma-accent)', color: '#FFF', flex: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>▤</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>اسکن</span>
        </button>

        {/* Armed, not toggled-on-forever: the next item picked asks for its
            weight, then the mode releases itself. */}
        <button
          onClick={onWeigh}
          style={{
            width: tall ? 84 : 72, height: tall ? 60 : 52, borderRadius: 12,
            background: weighing ? 'var(--luma-accent-tint)' : 'var(--luma-card)',
            border: `1px solid ${weighing ? 'var(--luma-accent)' : 'var(--luma-line-strong)'}`,
            boxShadow: weighing ? '0 0 0 4px rgba(249,124,21,.14)' : undefined,
            color: weighing ? 'var(--luma-accent-tint-text)' : 'var(--pos-ink-dark)', flex: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚖</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>وزن</span>
        </button>
      </div>

      {/* --- sold past zero, announced at the add ------------------------- */}
      {oversoldAdd && (
        <div
          style={{
            flex: 'none', background: 'var(--luma-tint-warning-bg)',
            border: '1px solid var(--luma-tint-warning-border)', borderRadius: 12,
            padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div
            style={{
              width: 32, height: 32, borderRadius: 9, background: 'var(--pos-accent-deep)',
              display: 'grid', placeItems: 'center',
              color: 'var(--luma-accent-tint-text)', fontWeight: 800,
            }}
          >
            !
          </div>
          <div style={{ flex: 1, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              «{oversoldAdd}» تمام شد — به سبد اضافه شد
            </div>
            <div style={{ fontSize: 12.5, color: '#8A6A4A' }}>
              فروش انجام می‌شود و در پنل به‌عنوان فروش با کسری علامت می‌خورد.
            </div>
          </div>
        </div>
      )}

      {/* --- barcode not in the catalogue --------------------------------- */}
      {notFoundCode && (
        <div
          style={{
            flex: 'none', background: 'var(--luma-tint-danger-bg)',
            border: '1px solid var(--luma-tint-danger-border)', borderRadius: 12,
            padding: 14, display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div
            style={{
              width: 34, height: 34, borderRadius: 9, background: 'var(--pos-danger-chip)',
              display: 'grid', placeItems: 'center', color: 'var(--luma-tint-danger-text)', fontWeight: 800,
            }}
          >
            ✕
          </div>
          <div style={{ flex: 1, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              بارکد «{fa(notFoundCode)}» در کاتالوگ نیست
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--pos-danger-body)' }}>
              کالا را در پنل بسازید تا اینجا قابل فروش شود. فروش با قیمت دستی روی این صندوق فعال نیست.
            </div>
          </div>
          <button
            onClick={onDismissNotFound}
            style={{
              height: 44, padding: '0 14px', borderRadius: 10,
              border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
              fontWeight: 700, fontSize: 13.5,
            }}
          >
            بستن
          </button>
        </div>
      )}

      {/* --- results, while searching ------------------------------------- */}
      {searching && (
        <div
          style={{
            flex: 1, minHeight: 0, background: 'var(--luma-card)',
            border: '1px solid var(--luma-line)', borderRadius: 12, overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              borderBottom: '1px solid var(--pos-rule)', fontSize: 12, fontWeight: 700,
              color: 'var(--luma-muted)',
            }}
          >
            <div style={{ flex: 1 }}>
              {matches.length
                ? `${fa(matches.length)} نتیجه برای «${query}»`
                : 'نتیجه‌ای پیدا نشد'}
            </div>
            <div style={{ width: 120 }}>موجودی</div>
            <div style={{ width: 140 }}>قیمت</div>
            <div style={{ width: 52 }} />
          </div>
          {matches.map((m) => {
            const chip = stockChip(m);
            return (
              <button
                key={m.variantId}
                onClick={() => add(m)}
                aria-label={`${m.name} — ${toman(m.priceRial)}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderBottom: '1px solid var(--pos-rule-faint)',
                  textAlign: 'right', minHeight: 56,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--luma-muted)' }}>
                    {m.barcode ? `کد ${fa(m.barcode)} · ` : ''}
                    {m.category ?? 'بدون دسته'} ·{' '}
                    {m.vatBasisPoints > 0 ? 'مشمول مالیات' : 'معاف از مالیات'}
                  </div>
                </div>
                <div style={{ width: 120, fontSize: 13, color: 'var(--luma-ink-2)' }}>{chip.label}</div>
                <div className="pos-num" style={{ width: 140, fontWeight: 700, fontSize: 15 }}>
                  {toman(m.priceRial)}
                </div>
                <div style={{ width: 52, display: 'flex', justifyContent: 'flex-end' }}>
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 9, background: 'var(--luma-accent-tint)',
                      color: 'var(--luma-accent-hover)', display: 'grid', placeItems: 'center',
                      fontSize: 18, fontWeight: 700,
                    }}
                  >
                    +
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* --- browsing ------------------------------------------------------ */}
      {weighing && (
        <div
          style={{
            flex: 'none', background: 'var(--luma-accent-tint)',
            border: '1px solid var(--luma-tint-warning-border)', borderRadius: 12,
            padding: '10px 14px', fontSize: 13, fontWeight: 600,
            color: 'var(--luma-accent-tint-text)',
          }}
        >
          کالای وزنی را انتخاب کنید — وزن را از روی ترازو بخوانید و وارد کنید.
        </div>
      )}

      {!searching && (
        <div style={{ flex: 'none', display: 'flex', gap: 8, overflow: 'auto', paddingBottom: 2, alignItems: 'center' }}>
          {/*
            Layout is a shop preference, not a per-sale action: a grocer with
            four thousand SKUs lives in search, a café of twenty drinks wants
            tiles. Set once, remembered on the device.
          */}
          <div
            style={{
              display: 'flex', gap: 4, background: 'var(--luma-surface-hover)',
              borderRadius: 10, padding: 4, flex: 'none',
            }}
          >
            {([
              ['search', 'جست‌وجو'], ['grid', 'کاشی'], ['list', 'فهرست'],
            ] as [Layout, string][]).map(([k, label]) => (
              <button
                key={k}
                onClick={() => onLayout(k)}
                style={{
                  height: 36, padding: '0 11px', borderRadius: 8, fontSize: 12.5,
                  fontWeight: 700, whiteSpace: 'nowrap',
                  background: layout === k ? 'var(--luma-card)' : 'transparent',
                  color: layout === k ? 'var(--luma-ink)' : 'var(--luma-muted)',
                  boxShadow: layout === k ? '0 1px 2px rgba(32,30,29,.08)' : undefined,
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ width: 1, height: 28, background: 'var(--luma-line)', flex: 'none' }} />
          {categories.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id}
                onClick={() => onCategory(c.id)}
                style={{
                  height: 44, padding: '0 16px', borderRadius: 10, fontSize: 13.5,
                  fontWeight: 700, whiteSpace: 'nowrap',
                  border: `1px solid ${active ? 'var(--luma-accent)' : 'var(--luma-line-strong)'}`,
                  background: active ? 'var(--luma-accent-tint)' : 'var(--luma-card)',
                  color: active ? 'var(--luma-accent-tint-text)' : 'var(--luma-ink-2)',
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {!searching && loading && (
        <div
          style={{
            flex: 1, minHeight: 0, display: 'grid', gap: 10, alignContent: 'start',
            gridTemplateColumns: 'repeat(auto-fill,minmax(156px,1fr))',
          }}
        >
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              style={{
                minHeight: 116, padding: 12, background: 'var(--luma-card)',
                border: '1px solid var(--pos-rule)', borderRadius: 12,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                overflow: 'hidden', position: 'relative',
              }}
            >
              <div style={{ height: 11, width: '36%', background: 'var(--pos-rule)', borderRadius: 5 }} />
              <div style={{ height: 14, width: `${55 + ((i * 7) % 35)}%`, background: 'var(--pos-rule)', borderRadius: 5 }} />
              <div style={{ height: 16, width: '52%', background: 'var(--luma-line)', borderRadius: 5 }} />
              <div
                style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.85),transparent)',
                  animation: 'lumaSweep 1.3s ease-in-out infinite',
                }}
              />
            </div>
          ))}
        </div>
      )}

      {!searching && !loading && layout !== 'list' && (
        <div
          style={{
            flex: 1, minHeight: 0, overflow: 'auto', display: 'grid', gap: 10,
            alignContent: 'start',
            /* auto-fill, not a fixed column count: the frame fills the
               board now, and a fixed 4 or 5 on a 1920 screen gives absurdly
               wide tiles instead of more products. The minimums are the widths
               those counts produced at the design's 1280. */
            gridTemplateColumns: `repeat(auto-fill,minmax(${layout === 'grid' ? 198 : 156}px,1fr))`,
          }}
        >
          {browsing.map((p) => {
            const chip = stockChip(p);
            return (
              <button
                key={p.variantId}
                onClick={() => add(p)}
                /* The tile's text lives in nested divs, so without this the
                   button has no accessible name at all — unreadable to a
                   screen reader and unaddressable by any test. */
                aria-label={`${p.name} — ${toman(p.priceRial)}`}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'right',
                  minHeight: layout === 'grid' ? 148 : 116, padding: 12,
                  background: 'var(--luma-card)', border: '1px solid var(--luma-line)',
                  borderRadius: 12, boxShadow: '0 1px 2px rgba(32,30,29,.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--luma-placeholder)' }}>
                    {p.barcode ? fa(p.barcode) : p.sku ? fa(p.sku) : ''}
                  </div>
                  <div style={chip.style}>{chip.short}</div>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  {/* Keyed by the URL so a product whose photograph was replaced
                      in the panel retries instead of staying hidden by the
                      previous one's failure. */}
                  <Thumb key={p.imageUrl} url={p.imageUrl} size={layout === 'grid' ? 48 : 40} />
                  <div
                    style={{
                      flex: 1, minWidth: 0, fontWeight: 600,
                      fontSize: 15, lineHeight: 1.35, textWrap: 'pretty' as never,
                    }}
                  >
                    {p.name}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                  <div className="pos-num" style={{ fontWeight: 800, fontSize: 15 }}>{toman(p.priceRial)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!searching && !loading && layout === 'list' && (
        <div
          style={{
            flex: 1, minHeight: 0, background: 'var(--luma-card)',
            border: '1px solid var(--luma-line)', borderRadius: 12, overflow: 'auto',
          }}
        >
          {browsing.map((p) => {
            const chip = stockChip(p);
            return (
              <button
                key={p.variantId}
                onClick={() => add(p)}
                aria-label={`${p.name} — ${toman(p.priceRial)}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '0 14px', height: 52, borderBottom: '1px solid var(--pos-rule-faint)',
                  textAlign: 'right',
                }}
              >
                <div style={{ width: 66, fontSize: 12, fontWeight: 700, color: 'var(--luma-placeholder)' }}>
                  {p.barcode ? fa(p.barcode) : ''}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                <div style={{ width: 118, fontSize: 12.5, color: 'var(--luma-muted)' }}>{p.category ?? ''}</div>
                <div style={{ width: 110, fontSize: 13, color: 'var(--luma-ink-2)' }}>{chip.label}</div>
                <div className="pos-num" style={{ width: 140, fontWeight: 700, fontSize: 15 }}>{toman(p.priceRial)}</div>
                <div
                  style={{
                    width: 36, height: 36, borderRadius: 9, background: 'var(--luma-accent-tint)',
                    color: 'var(--luma-accent-hover)', display: 'grid', placeItems: 'center',
                    fontSize: 18, fontWeight: 700,
                  }}
                >
                  +
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
